import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type GameSessionControls = {
  canReset: boolean;
  isGameOver: boolean;
  leaving: boolean;
  resetting: boolean;
  requestLeave: () => void;
  resetGame?: () => void;
};

type GameSessionFlags = {
  canReset: boolean;
  isGameOver: boolean;
  leaving: boolean;
  resetting: boolean;
};

type GameSessionContextValue = {
  session: GameSessionControls | null;
  setSession: (session: GameSessionControls | null) => void;
};

const GameSessionContext = createContext<GameSessionContextValue>({
  session: null,
  setSession: () => {},
});

export function useGameSession() {
  return useContext(GameSessionContext);
}

function flagsEqual(a: GameSessionFlags | null, b: GameSessionFlags) {
  return (
    !!a &&
    a.canReset === b.canReset &&
    a.isGameOver === b.isGameOver &&
    a.leaving === b.leaving &&
    a.resetting === b.resetting
  );
}

export default function GameSessionProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [flags, setFlags] = useState<GameSessionFlags | null>(null);
  const requestLeaveRef = useRef<() => void>(() => {});
  const resetGameRef = useRef<(() => void) | undefined>(undefined);

  const setSession = useCallback((next: GameSessionControls | null) => {
    if (!next) {
      requestLeaveRef.current = () => {};
      resetGameRef.current = undefined;
      setFlags((prev) => (prev === null ? prev : null));
      return;
    }
    requestLeaveRef.current = next.requestLeave;
    resetGameRef.current = next.resetGame;
    const nextFlags: GameSessionFlags = {
      canReset: next.canReset,
      isGameOver: next.isGameOver,
      leaving: next.leaving,
      resetting: next.resetting,
    };
    setFlags((prev) => (flagsEqual(prev, nextFlags) ? prev : nextFlags));
  }, []);

  const requestLeave = useCallback(() => {
    requestLeaveRef.current();
  }, []);
  const resetGame = useCallback(() => {
    resetGameRef.current?.();
  }, []);

  const session = useMemo<GameSessionControls | null>(() => {
    if (!flags) return null;
    return {
      ...flags,
      requestLeave,
      resetGame: flags.canReset ? resetGame : undefined,
    };
  }, [flags, requestLeave, resetGame]);

  const value = useMemo(() => ({ session, setSession }), [session, setSession]);

  return (
    <GameSessionContext.Provider value={value}>
      {children}
    </GameSessionContext.Provider>
  );
}
